import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { ButtonLink } from '@/components/ui/Link';
import { Spinner } from '@/components/ui/Spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  AuditLog,
  AuditLogBoolExp,
  AuditLogInsertInput,
  AuditLogOrderBy,
  Customer,
  OrderBy,
  Site,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ColumnDef,
  PaginationState,
  SortingState,
} from '@tanstack/react-table';
import { Navigation, Trash } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  deleteCustomerMutation,
  deleteSitesMutation,
} from '../graphql/mutation';
import { customerByPkQuery } from '../graphql/query';

import { useConfirmModal } from '@/components/ConfirmModalProvider';
import { DataTable } from '@/components/DataTable';
import { Checkbox } from '@/components/ui/Checkbox';
import { ErrorBoundaryFallbackRender } from '@/components/ui/ErrorBoundaryFallback';
import { auditLogByEntityQuery } from '@/features/Devices/graphql/query';
import useDataTable from '@/hooks/useDataTable';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { FeatureName } from '@/models/role.models';
import {
  createAuditLogInput,
  createAuditLogTitle,
} from '@/utils/audit-log.function';
import hasPermission from '@/utils/permission.function';
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'sonner';
import { CustomerApiKeysSection } from '../components/CustomerApiKeysSection';
import { CustomerConfigTab } from '../components/edit-customer-tabs/CustomerConfigTab';

const siteColumns: ColumnDef<Site>[] = [
  {
    id: 'select',
    cell: ({ row }) => {
      return (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      );
    },
    size: 50,
  },
  {
    accessorKey: 'name',
    header: 'Site Name',
  },
  {
    accessorKey: 'assignedDevices',
    header: 'Number of Devices',
    cell: ({ row }) => row.original.devices?.length,
  },
  {
    accessorKey: 'action',
    header: '',
    cell: ({ row }) => (
      <ButtonLink
        variant="outline"
        className="ms-auto flex"
        size={'icon-sm'}
        to={`/sites/${row.original.id}`}
      >
        <Navigation />
      </ButtonLink>
    ),
  },
];

const EditCustomerPage: React.FC = () => {
  const { id } = useParams();
  const [customer, setCustomer] = useState<Customer>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const navigate = useNavigate();

  const [auditLogFilters, _setAuditLogFilters] = useState<{
    where: AuditLogBoolExp;
    orderBy: AuditLogOrderBy[];
  }>({
    orderBy: [{ createdAt: OrderBy.Desc }],
    where: {
      _and: [{ entities: { _contains: [{ id: id }] } }],
    },
  });

  const [auditHistory, setAuditHistory] = useState<AuditLog[]>();

  const { data: auditLogData, refetch: refetchLog } = useQuery({
    queryKey: ['AuditLog', auditLogFilters],
    queryFn: () =>
      execute({
        query: auditLogByEntityQuery,
        variables: {
          where: auditLogFilters.where,
          orderBy: auditLogFilters.orderBy,
        },
      }),
    select: (data) => data,
  });

  useEffect(() => {
    if (auditLogData) {
      setAuditHistory(auditLogData.auditLog as AuditLog[]);
    }
  }, [auditLogData]);

  const { data, refetch } = useQuery({
    queryKey: ['CustomerByPkQuery', id],
    queryFn: () =>
      execute({
        query: customerByPkQuery,
        variables: { id: id as string },
      }),
    select: (data) => data,
    staleTime: 5000,
  });

  useEffect(() => {
    if (data) {
      setCustomer(data.customerByPk as Customer);
    }
  }, [data]);

  const table = useDataTable<Site>({
    data: customer?.sites ?? [],
    columns: siteColumns,
    rowCount: customer?.sitesAggregate?.aggregate?.count ?? 0,
    sorting,
    pagination,
    setSorting,
    setPagination,
  });

  const { mutateAsync: deleteCustomer } = useMutation({
    mutationKey: ['DeleteCustomer'],
    mutationFn: (options: { id: string; auditLogInput: AuditLogInsertInput }) =>
      execute({ query: deleteCustomerMutation, variables: { ...options } }),
    onSuccess: () => {
      toast.success('Customer deleted successfully!', { duration: 3000 });
      void navigate('/customers');
    },
  });

  const { mutateAsync: deleteSites } = useMutation({
    mutationKey: ['DeleteSites'],
    mutationFn: (options: {
      ids: string[];
      auditLogInput: AuditLogInsertInput;
    }) => execute({ query: deleteSitesMutation, variables: { ...options } }),
    onSuccess: async () => {
      await refetch();
      toast.success('Sites deleted successfully!', { duration: 3000 });
    },
  });

  const { openModal } = useConfirmModal();

  const confirmDeleteSites = () => {
    const canDelete = table.getSelectedRowModel().rows.every((row) => {
      return row.original.devices && row.original.devices.length === 0;
    });

    const listOfSites = table
      .getSelectedRowModel()
      .rows.map((row) => row.original.name);
    const message = canDelete ? (
      <span>
        Are you sure you want to delete the following Sites:
        {listOfSites.map((name) => (
          <div key={name}>
            <strong>{name}</strong>
          </div>
        ))}
      </span>
    ) : (
      <span>
        Some selected Sites have associated devices. Please remove the devices
        before deleting the Sites.
      </span>
    );

    if (canDelete) {
      openModal({ message }, async () => {
        const ids = table
          .getSelectedRowModel()
          .rows.map((row) => row.original.id);
        await deleteSites({
          ids,
          auditLogInput: createAuditLogInput({
            action: AuditLogAction.DELETE_SITE,
            entities: [
              {
                id: data?.customerByPk?.id as string,
                entity: AuditLogEntityType.CUSTOMER,
              },
              ...ids.map((id) => ({ id, entity: AuditLogEntityType.SITE })),
            ],
          }),
        });
        table.resetRowSelection();
      });
    } else {
      openModal({
        message,
        title: 'Delete Sites',
        cancelText: 'Close',
        showConfirm: false,
      });
    }
  };

  const handleDeleteCustomerClick = () => {
    const canDeleteCustomer =
      customer?.apiKeys.length === 0 &&
      customer?.sitesAggregate?.aggregate?.count === 0;

    const message = canDeleteCustomer ? (
      <span>
        Are you sure you want to delete customer{' '}
        <strong>{customer?.name}</strong>?
      </span>
    ) : (
      <span>
        Customer <strong>{customer?.name}</strong> has associated API keys or
        sites. Please remove them before deleting the customer.
      </span>
    );

    if (canDeleteCustomer) {
      openModal({ message }, async () => {
        await deleteCustomer({
          id: data?.customerByPk?.id as string,
          auditLogInput: createAuditLogInput({
            action: AuditLogAction.DELETE_CUSTOMER,
            entities: [
              {
                id: data?.customerByPk?.id as string,
                entity: AuditLogEntityType.CUSTOMER,
              },
            ],
          }),
        });
      });
    } else {
      openModal({
        message,
        title: 'Delete Customer',
        cancelText: 'Close',
        showConfirm: false,
      });
    }
  };

  return (
    <ErrorBoundary fallbackRender={ErrorBoundaryFallbackRender}>
      {customer ? (
        <>
          <div className="flex justify-between align-middle">
            <div className="flex items-center space-x-2">
              <span className="text-xl font-semibold tracking-tight">
                {customer.name}
              </span>
            </div>
            <div className="flex gap-2">
              {hasPermission(FeatureName.DELETE_CUSTOMER) && (
                <Button
                  variant={'destructive'}
                  size={'sm'}
                  onClick={handleDeleteCustomerClick}
                >
                  <Trash />
                  Delete
                </Button>
              )}
            </div>
          </div>
          <Tabs defaultValue={'general'} className="grid rounded-xl">
            <TabsList className="grid w-[480px] grid-cols-4">
              <TabsTrigger value="general">Config</TabsTrigger>
              <TabsTrigger value="sites">Sites</TabsTrigger>
              {hasPermission(FeatureName.VIEW_API_KEYS) && (
                <TabsTrigger value="apiKeys">Api Keys</TabsTrigger>
              )}
              {hasPermission(FeatureName.VIEW_AUDIT_HISTORY) && (
                <TabsTrigger value="auditHistory">Audit History</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="general">
              <CustomerConfigTab
                customer={customer}
                refetch={refetch}
                refetchLog={refetchLog}
              />
            </TabsContent>
            <TabsContent value="sites">
              <Card className="relative flex flex-col gap-4">
                <CardHeader>
                  <CardTitle>Sites</CardTitle>
                  <CardDescription className="truncate">
                    Sites assigned to the customer.
                  </CardDescription>
                  <Button
                    className="xs:top-4 absolute top-2 right-4"
                    variant={'destructive'}
                    size={'sm'}
                    onClick={confirmDeleteSites}
                    disabled={table.getSelectedRowModel().rows.length === 0}
                  >
                    <Trash />
                    Delete Selected Sites
                  </Button>
                </CardHeader>
                <CardContent>
                  <DataTable table={table} selectRowEnabled={true} />
                </CardContent>
              </Card>
            </TabsContent>
            {hasPermission(FeatureName.VIEW_API_KEYS) && (
              <TabsContent value="apiKeys">
                <CustomerApiKeysSection
                  customerId={customer.id}
                  apiKeys={customer.apiKeys ?? []}
                  refetch={refetch}
                  refetchLog={refetchLog}
                />
              </TabsContent>
            )}
            {hasPermission(FeatureName.VIEW_AUDIT_HISTORY) && (
              <TabsContent value="auditHistory">
                <Card>
                  <CardHeader>
                    <CardTitle>Audit History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid max-h-[10rem] overflow-y-auto">
                      {auditHistory?.map((audit) => (
                        <div key={audit.id}>{createAuditLogTitle(audit)}</div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </>
      ) : (
        <div className="flex h-full flex-grow items-center justify-center">
          <Spinner />
        </div>
      )}
    </ErrorBoundary>
  );
};

export default EditCustomerPage;
